import { useCallback, useMemo, useState } from 'react';
import { useApp } from '../../../context/AppContext';
import SchoolAPI from '../../../api/school';
import logger from '../../../utils/logger';

const formatDateKey = (key) => {
    // Intenta dejar una etiqueta "amigable" si el backend manda keys tipo ISO/date-string
    if (!key) return '';
    const d = new Date(key);
    if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString('es-CL');
    }
    return key;
};

const safeId = (obj) => obj?.id || obj?._id;

const normalizeBootcampsList = (payload) => {
    // Casos comunes
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== 'object') return [];

    // Estilo { success: true, data: [...] }
    if (payload.success && Array.isArray(payload.data)) return payload.data;

    // Estilo { success: true, data: { bootcamps: [...] } }
    if (payload.success && Array.isArray(payload.data?.bootcamps)) return payload.data.bootcamps;

    // Estilo { bootcamps: [...] }
    if (Array.isArray(payload.bootcamps)) return payload.bootcamps;

    // Estilo { data: [...] }
    if (Array.isArray(payload.data)) return payload.data;

    // Estilo { data: { bootcamps: [...] } }
    if (Array.isArray(payload.data?.bootcamps)) return payload.data.bootcamps;

    // Variantes genéricas
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.results)) return payload.results;

    return [];
};

const normalizeBootcampDetail = (payload) => {
    // Queremos retornar el bootcamp "plano"
    if (!payload) return null;
    if (typeof payload !== 'object') return null;

    // Caso: el backend retorna el objeto directamente
    if (payload?.students || payload?.title || payload?._id || payload?.id) return payload;

    // Estilo { success: true, data: {...} }
    if (payload.success && payload.data && typeof payload.data === 'object') return payload.data;

    // Estilo { data: {...} }
    if (payload.data && typeof payload.data === 'object') return payload.data;

    // Estilo { bootcamp: {...} }
    if (payload.bootcamp && typeof payload.bootcamp === 'object') return payload.bootcamp;

    // Estilo { data: { bootcamp: {...} } }
    if (payload.data?.bootcamp && typeof payload.data.bootcamp === 'object') return payload.data.bootcamp;

    return payload;
};

/**
 * Hook para Bootcamps (Academia)
 * - Listado de bootcamps
 * - Detalle (estudiantes + métricas de asistencia)
 * - Creación de asistencia
 */
export const useBootcamps = () => {
    const { notify } = useApp();

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleError = useCallback((err, defaultMessage) => {
        const msg = err?.response?.data?.message || err?.message || defaultMessage || 'Error en la operación';
        logger.error('[Bootcamps]', err);
        setError(msg);
        notify({ type: 'error', title: 'Error', text: msg });
    }, [notify]);

    const handleSuccess = useCallback((message) => {
        setError(null);
        notify({ type: 'success', title: 'Éxito', text: message || 'Operación exitosa' });
    }, [notify]);

    const getBootcamps = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await SchoolAPI.getAllBootcamps();
            const list = normalizeBootcampsList(data);

            // Debug (solo dev) para confirmar shape cuando vuelva a ocurrir
            if (process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line no-console
                console.log('[Bootcamps] getAllBootcamps payload:', data);
                // eslint-disable-next-line no-console
                console.log('[Bootcamps] normalized list length:', list?.length);
            }

            return list;
        } catch (err) {
            handleError(err, 'Error al obtener bootcamps');
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const getBootcampById = useCallback(async (id) => {
        if (!id) return null;
        setIsLoading(true);
        setError(null);
        try {
            const data = await SchoolAPI.getBootcampsById(id);
            const bootcamp = normalizeBootcampDetail(data);

            if (process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line no-console
                console.log('[Bootcamps] getBootcampsById payload:', data);
                // eslint-disable-next-line no-console
                console.log('[Bootcamps] normalized bootcamp:', bootcamp);
            }

            return bootcamp;
        } catch (err) {
            handleError(err, 'Error al obtener el bootcamp');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const createAttendance = useCallback(async (payload) => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await SchoolAPI.createAttendance(payload);
            handleSuccess('Asistencia creada correctamente');
            return res;
        } catch (err) {
            handleError(err, 'Error al crear la asistencia');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    const buildStudentsTable = useCallback((bootcamp) => {
        const students = Array.isArray(bootcamp?.students) ? bootcamp.students : [];

        const baseColumns = [
            { key: 'username', label: 'Nombre' },
            { key: 'email', label: 'Correo electrónico' },
            { key: 'phone', label: 'Teléfono' },
            { key: 'asistencia', label: '% Asistencia', style: { textAlign: 'center' } },
        ];

        // reunir todas las keys de asistencia (fechas) de todos los estudiantes
        const attendanceKeys = new Set();
        for (const st of students) {
            const attendances = st?.attendances && typeof st.attendances === 'object' ? st.attendances : {};
            Object.keys(attendances).forEach((k) => attendanceKeys.add(k));
        }

        const extraColumns = Array.from(attendanceKeys)
            .sort((a, b) => {
                const da = new Date(a).getTime();
                const db = new Date(b).getTime();
                if (!Number.isNaN(da) && !Number.isNaN(db)) return da - db;
                return String(a).localeCompare(String(b));
            })
            .map((k) => ({ key: k, label: formatDateKey(k), style: { textAlign: 'center' } }));

        const columns = [...baseColumns, ...extraColumns];

        const customElements = {
            asistencia: (row) => (
                <div style={{ textAlign: 'center', fontWeight: 600 }}>
                    {row.asistencia}
                </div>
            ),
            phone: (row) => row?.fields?.cf_numerodetelefono || '-',
        };

        // UI simple para booleanos (tick / cross)
        const renderBool = (value) => (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {value ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z" stroke="#2ecc71" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M7.75 12L10.58 14.83L16.25 9.17004" stroke="#2ecc71" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z" stroke="#e74c3c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9.16998 14.83L14.83 9.17004" stroke="#e74c3c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M14.83 14.83L9.16998 9.17004" stroke="#e74c3c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )}
            </div>
        );

        for (const col of extraColumns) {
            const k = col.key;
            if (!customElements[k]) {
                customElements[k] = (row) => renderBool(!!row?.[k]);
            }
        }

        const rows = students.map((st) => {
            const attendances = st?.attendances && typeof st.attendances === 'object' ? st.attendances : {};
            const values = Object.values(attendances);
            const total = values.length;
            const present = values.filter(Boolean).length;
            const pct = total > 0 ? Math.round((present / total) * 100) : 0;

            const row = {
                ...st,
                id: safeId(st),
                asistencia: `${pct}%`,
            };

            for (const [k, v] of Object.entries(attendances)) {
                row[k] = v;
            }
            return row;
        });

        return { columns, rows, customElements };
    }, []);

    const listColumns = useMemo(() => ([
        { key: 'title', label: 'Nombre' },
        { key: 'max_number_of_users', label: 'Número máximo de usuarios' },
        { key: 'tags', label: 'Tags' },
        { key: 'actions', label: 'Acciones' },
    ]), []);

    return {
        // Estado
        isLoading,
        error,

        // API
        getBootcamps,
        getBootcampById,
        createAttendance,

        // Helpers UI
        listColumns,
        buildStudentsTable,
        safeId,
    };
};

export default useBootcamps;


